import React from "react";
import AmlDocumentUploadModal, {
  AmlDocumentUploadModalProps,
  DocumentStatus,
} from "./AmlDocumentUploadModal";
import useBoolean from "../hooks/useBoolean";
import { RawFile, SavedFile } from "../model";
import { bytesToSize } from "../utils/file";
import { DocAPI } from "../api";
import useMessage from "../hooks/useMessage";
import AmlDocument from "../components/AmlDocument";
import Text from "../components/Text";
import {
  Box,
  Button,
  IconButton,
  SxProps,
  Theme,
  Tooltip,
} from "@mui/material";
import { EmptyPage, Plus, Xmark } from "iconoir-react";
import FilePreview from "../components/FilePreview";
import { isNumber } from "lodash";
import Empty from "../components/Empty";

type TFile = (SavedFile | RawFile) & {
  status?: (typeof DocumentStatus)[keyof typeof DocumentStatus];
};

function processNewDoc(doc: TFile) {
  const newDocument = {
    ...doc,
  };
  if (isRemoteDoc(doc)) {
    newDocument.status = DocumentStatus.SUCCESS;
  }
  if (isLocalDoc(doc)) {
    newDocument.status = doc.status || DocumentStatus.PENDING;
  }
  return newDocument;
}

function isLocalDoc(doc: TFile): doc is RawFile {
  return (doc as any)._raw !== undefined;
}

function isRemoteDoc(doc: TFile): doc is SavedFile {
  return !isLocalDoc(doc);
}

type DocumentDisplayProps = {
  doc: TFile;
  disabled?: boolean;
  removable?: boolean;
  onRemove?: (file: TFile) => void;
};

const DocumentDisplay: React.FC<DocumentDisplayProps> = ({
  doc,
  disabled,
  removable,
  onRemove,
}) => {
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const message = useMessage();
  React.useEffect(() => {
    return () => {
      if (isLocalDoc(doc) && previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, []);
  const handleRemove = () => {
    onRemove && onRemove(doc);
  };
  return (
    <AmlDocument.Root key={isLocalDoc(doc) ? doc.uid : doc.urn}>
      <AmlDocument.Status status={doc.status as any} />
      <AmlDocument.Property label="File properties" sx={{ width: "140px" }}>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              display: "flex",
              width: "100%",
              gap: "4px",
              cursor: "pointer",
            }}
          >
            <EmptyPage
              style={{
                fill: "#7e7e7e",
                strokeWidth: "0",
                fontSize: "0.9rem",
                flexShrink: 0,
              }}
            />
            <FilePreview filename={doc.name}>
              {({ previewLocalFile, previewRemoteFile, loading }) => {
                return (
                  <Tooltip title={`Preview ${doc.name}`} placement="top">
                    <Text
                      ellipsis={1}
                      sx={{
                        fontSize: "0.9em",
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        if (loading) {
                          return;
                        }
                        isLocalDoc(doc)
                          ? previewLocalFile(doc._raw)
                          : previewRemoteFile(doc.urn);
                      }}
                    >
                      {doc.name}
                    </Text>
                  </Tooltip>
                );
              }}
            </FilePreview>
          </Box>
          {isLocalDoc(doc) && (
            <Text
              italic
              sx={{
                marginLeft: "1rem",
                paddingLeft: "4px",
                fontSize: "0.8em",
              }}
            >
              {bytesToSize(doc.size)}
            </Text>
          )}
        </Box>
      </AmlDocument.Property>
      <AmlDocument.Property label="Uploader">
        <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
          <Text
            sx={{ fontSize: "0.9em", lineHeight: "0.9em" }}
          >{`${doc.uploader}`}</Text>
          {doc.uploadTime && (
            <Text
              sx={{
                fontSize: "0.8em",
                lineHeight: "0.8em",
                fontStyle: "italic",
              }}
            >
              {doc.uploadTime}
            </Text>
          )}
        </div>
      </AmlDocument.Property>
      <AmlDocument.Property label="Category">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "3px",
            maxWidth: "140px",
          }}
        >
          <Text
            sx={{ fontSize: "0.9em", lineHeight: "0.9em" }}
          >{`${doc.amlCategoryDescription}`}</Text>
          {doc.otherTypeDescription && (
            <Text
              ellipsis={1}
              italic
              sx={{
                fontSize: "0.8em",
                lineHeight: "0.8em",
              }}
            >{`${doc.otherTypeDescription}`}</Text>
          )}
        </Box>
      </AmlDocument.Property>
      {removable && !disabled && (
        <AmlDocument.Controller>
          <IconButton
            sx={{
              height: "24px",
              width: "28px",
              borderRadius: "4px",
              backgroundColor: "#ff4d4f",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#ff4d4f",
              },
            }}
            onClick={handleRemove}
          >
            <Xmark />
          </IconButton>
        </AmlDocument.Controller>
      )}
    </AmlDocument.Root>
  );
};

export type DocumentListRef = {
  uploadAll: () => Promise<Exclude<TFile, RawFile>[]>;
};

type DocumentListProps = {
  disabled?: boolean;
  maxHeight?: number;
  removable?: boolean;
  initialDocs?: TFile[];
  maxDocNumber?: number;
  sx?: SxProps<Theme>;
  allowUpload?: boolean;
  UploadModalProps?: Omit<
    AmlDocumentUploadModalProps,
    "open" | "maxFileNum" | "onClose" | "onComplete"
  >;
};
const DocumentList = React.forwardRef<DocumentListRef, DocumentListProps>(
  (
    {
      maxHeight = 180,
      disabled,
      removable,
      initialDocs = [],
      maxDocNumber = -1,
      sx = {},
      allowUpload = true,
      UploadModalProps,
    },
    ref
  ) => {
    const [documents, setDocuments] = React.useState(
      initialDocs.map(processNewDoc)
    );
    const [uploading, { setTrue: startUploading, setFalse: finishUploading }] =
      useBoolean(false);
    const [
      uploadModalOpen,
      { setTrue: showUploadModal, setFalse: closeUploadModal },
    ] = useBoolean();
    const message = useMessage();
    // ref to an empty div element. make it easier to scroll to bottom of document list.
    const bottomAnchorRef = React.useRef<HTMLDivElement | null>(null);
    const lastDocuments = React.useRef(initialDocs.map(processNewDoc));
    const latestMaxDocNumberRef = React.useRef(maxDocNumber);
    latestMaxDocNumberRef.current = maxDocNumber;
    const maxHeightStyle = isNumber(maxHeight) ? `${maxHeight}px` : maxHeight;

    const scrollToBottom = () => {
      if (bottomAnchorRef.current) {
        bottomAnchorRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    };

    React.useEffect(() => {
      // scroll to bottom when adding new documents.
      if (lastDocuments.current.length < documents.length) {
        scrollToBottom();
      }
      lastDocuments.current = documents;
    }, [documents]);

    const pushDocument = (newDoc: RawFile) => {
      if (disabled) {
        return;
      }
      setDocuments((pre) => {
        if (
          latestMaxDocNumberRef.current > 0 &&
          pre.length >= latestMaxDocNumberRef.current
        ) {
          message.error(
            `Sorry, you can only upload up to ${
              latestMaxDocNumberRef.current
            } file${latestMaxDocNumberRef.current > 1 ? "s" : ""}.`
          );
          // don't need rerender
          return pre;
        }
        const doc = processNewDoc(newDoc);
        const newDocuments = [...pre, doc];
        return newDocuments;
      });
    };

    const handleFileSelect = (rawFiles: RawFile[]) => {
      rawFiles.forEach(pushDocument);
    };
    const uploadAll = () => {
      startUploading();
      return Promise.all(
        documents.map((el) => {
          if (isLocalDoc(el)) {
            return uploadLocalDocument(el);
          } else {
            return uploadRemoteDocument(el);
          }
        })
      ).finally(() => {
        finishUploading();
      });
    };
    const setDocumentStatus = (
      id: string,
      status: keyof typeof DocumentStatus
    ) => {
      setDocuments((pre) => {
        const newDocuments = [...pre];
        const doc = newDocuments.find((el) => {
          return isLocalDoc(el) ? el.uid === id : el.urn === id;
        });
        doc && (doc.status = status);
        return newDocuments;
      });
    };
    const updateDocument = (id: string, newDoc: TFile) => {
      setDocuments((pre) => {
        const newDocuments = [...pre];
        const index = newDocuments.findIndex((el: any) => {
          return el.uid === id || el.urn === id;
        });
        if (index !== -1) {
          newDocuments.splice(index, 1, newDoc);
        }
        return newDocuments;
      });
    };
    const uploadLocalDocument = (
      doc: RawFile
    ): Promise<Exclude<TFile, RawFile>> => {
      setDocumentStatus(doc.uid, DocumentStatus.UPLOADING);
      const data = new FormData();
      data.append("file", doc._raw);
      data.append("public", String(Boolean(doc.isPublic)));
      data.append("metadata", JSON.stringify(doc.metadata));
      return DocAPI.upload(doc._raw, {
        isPublic: doc.isPublic,
        customerId: doc.metadata.customerId!,
        investorId: doc.metadata.investorId!,
        docVaultCategory: doc.metadata.category!,
      })
        .then((response) => {
          // now this document is a remote document
          delete (doc as any)._raw;
          const updatedDoc: Exclude<TFile, RawFile> = {
            ...doc,
            urn: response.urn,
            status: DocumentStatus.SUCCESS,
          };
          updateDocument(doc.uid, updatedDoc);
          return updatedDoc;
        })
        .catch((error) => {
          setDocumentStatus(doc.uid, DocumentStatus.ERROR);
          console.error(error);
          message.error(
            `Failed to upload ${doc.name}. Please remove the file or try again.`
          );
          throw error;
        });
    };
    const uploadRemoteDocument = (doc: SavedFile): Promise<SavedFile> => {
      // remote document don't need upload again. So just resolve document itself.
      return Promise.resolve(doc);
    };
    React.useImperativeHandle(ref, () => {
      return {
        // provide upload method, it's common behaviour.
        uploadAll: uploadAll,
      };
    });
    const handleRemoveDoc = (doc: TFile) => {
      const newDocuments = documents.filter((el) => {
        return doc !== el;
      });
      setDocuments(newDocuments);
    };
    const fileExceedLimit =
      maxDocNumber > 0 && documents.length >= maxDocNumber;
    return (
      <>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            maxHeight: maxHeightStyle,
            width: "100%",
            padding: "6px 10px",
            borderRadius: "4px",
            overflow: "auto",
            border: "1px dashed #dcdcdc",
            ...(allowUpload ? { borderBottom: "none" } : {}),
            ...sx,
          }}
        >
          {documents.length === 0 ? (
            <Empty description="" />
          ) : (
            documents.map((el) => (
              <DocumentDisplay
                doc={el}
                removable={!disabled && removable}
                onRemove={handleRemoveDoc}
              />
            ))
          )}
          <div id="bottom-anchor" ref={bottomAnchorRef}></div>
        </Box>
        {allowUpload && (
          <>
            <Box
              sx={{
                height: "50px",
                width: "100%",
                border: "1px dashed #dcdcdc",
                borderTop: "none",
                background: "#f6f6f6",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                cursor: fileExceedLimit ? "not-allowed" : "pointer",
              }}
              onClick={() => !fileExceedLimit && showUploadModal()}
            >
              <Button
                sx={{ borderRadius: "2px", padding: "1px 9px" }}
                disabled={fileExceedLimit}
                startIcon={<Plus />}
              >
                Add File
              </Button>
            </Box>
            <AmlDocumentUploadModal
              {...UploadModalProps!}
              open={uploadModalOpen}
              maxFileNum={
                maxDocNumber < 0
                  ? maxDocNumber
                  : maxDocNumber - documents.length
              }
              onClose={closeUploadModal}
              onComplete={handleFileSelect}
            />
          </>
        )}
      </>
    );
  }
);

export default DocumentList;
